import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { cssGenerator } from '@/mixins/css-generator';
import { products } from '@/mixins/products';
import tooltip from '@/mixins/components/tooltip';
import { SQUASH_DROPLET } from '@/constants';
import { getDynamicData } from '@editor/page/top-control-bar/dynamic-data/item-types';
import DropletOptions from '../_bubble-options';
import TextBlobOptions from './components/_edit-options';
import schema from './droplet.schema';
const dropletSchema = schema();

export default Vue.component('text-blob', {
  template: require('./template.html'),
  name: 'text-blob',
  mixins: [cssGenerator, products],
  computed: {
    ...mapGetters([
      'currentPage',
      'currentSite',
      'viewport',
      'localStyle',
      'activeDroplet',
      'activeState',
      'animationPreview',
      'dropletObserver',
      'dragging',
      'globalBubbleOptionsOpen',
    ]),
    roamingTemplate() {
      return this.currentPage.type !== 'template' && this.droplet.templateRegion;
    },
    isActiveDroplet() {
      return this.activeDroplet._id === this.droplet._id;
    },
    hideTransform() {
      return this.dragging || this.globalBubbleOptionsOpen;
    },
    activeSiteType() {
      return this.droplet.siteType || this.currentSite.type;
    },
    dynamicData() {
      return getDynamicData({ type: this.activeSiteType }) || {};
    },
    storeData() {
      return this.currentSite.dynamic && this.currentSite.dynamic.products;
    },
    currencyType() {
      const enabled = this.storeData;
      const symbols = {
        USD: '&#36;',
        CAD: '&#36;',
        EUR: '&#128;',
      };
      const type = enabled && enabled.currency ? enabled.currency : 'USD';
      return {
        type: type.toLowerCase(),
        symbol: symbols[type],
      };
    },
    output() {
      let text = this.droplet.text;
      const { dynamicKey } = this.droplet;
      if (this.dynamicItem._id) {
        text = this.dynamicItem[dynamicKey];
        if (['publishedOn', 'released'].includes(dynamicKey)) {
          text = this._zoneFormat({
            date: text.date,
            zone: this.currentSite.timezone,
          });
        }
        if (dynamicKey === 'price') {
          const { symbol } = this.currencyType;
          const pricing = this.getPrices({
            product: this.dynamicItem,
            store: this.storeData,
          });
          const original = pricing.original ? `<span class="drzBlobText-strike">${symbol}${pricing.original}</span> ` : '';
          text = `${original}${symbol}${pricing.live}`;
        }
      }
      return text;
    },
    dynamicItem() {
      const enabled = this.currentSite.dynamic && this.currentSite.dynamic[this.droplet.siteType];
      const items = enabled ? (enabled.published || []) : [];
      const { latestDynamic, ref } = this.droplet;
      let item;
      if (ref && enabled && !latestDynamic) {
        item = ref && enabled ? _.find(items, p => p._id === ref) : false;
      }
      if (enabled && latestDynamic) {
        const sort = this.dynamicData.sort;
        item = items.reduce(
          (a, b) => (
            new Date((a[sort] && a[sort].date) || a.publishedOn) >
            new Date((b[sort] && b[sort].date) || b.publishedOn) ? a : b
          ),
        );
      }
      return item || {};
    },
    profile() {
      return this._dropletType(this.droplet);
    },
    displayStatus() {
      let settings = {
        desktop: 'default',
        tablet: 'default',
        mobile: 'default',
      };
      const selector = _.find(this.droplet.mediaQueries, { selector: ', .drzBlob' });
      if (selector) {
        const property = _.find(selector.properties, { property: 'display' });
        if (property) {
          settings = property;
        }
      }
      return settings;
    },
  },
  data() {
    return {
      type: dropletSchema.type,
      SQUASH_DROPLET,
      display: {
        top: null,
        left: null,
        right: null,
        bottom: null,
        ctPaddingTop: null,
        ctPaddingBottom: null,
        ctPaddingLeft: null,
        ctPaddingRight: null,
        btMarginTop: null,
        btMarginBottom: null,
        btMarginLeft: null,
        btMarginRight: null,
        transform: null,
        perspective: null,
        width: null,
        'line-height': null,
        'text-shadow': null,
        'font-size': null,
        'letter-spacing': null,
        'padding-left': null,
        'padding-right: ': null,
        'padding-top:': null,
        'padding-bottom': null,
        'border-left-width': null,
        'border-right-width': null,
        'border-top-width': null,
        'border-bottom-width': null,
        'border-top-left-radius': null,
        'border-top-right-radius': null,
        'border-bottom-left-radius': null,
        'border-bottom-right-radius': null,
      },
      hidden: false,
      dropletHovered: false,
      setBubbleSize: 'dropletBubble-normal',
      showEditBubble: false,
      showRevealClass: false,
    };
  },
  props: ['droplet', 'items', 'index'],
  components: {
    DropletOptions,
    TextBlobOptions,
    tooltip,
  },
  watch: {
    isActiveDroplet(is) {
      if (is) {
        this._scrollToDroplet(this.$el);
      }
    },
    'activeDroplet._id': function c(id) {
      if (!id) {
        this.showRevealClass = false;
      }
    },
    activeState(state) {
      if (state === ':reveal' && this.isActiveDroplet) {
        this.showRevealClass = true;
      } else {
        this.showRevealClass = false;
      }
    },
    animationPreview(should) {
      if (should && this.isActiveDroplet) {
        const droplet = this.$refs.dropletRow;
        const $droplet = $(droplet);
        $droplet.drzOnReveal({
          editing: this.isActiveDroplet,
          child: '.drzBlob',
        });
      }
    },
    viewport() {
      this.callCssGenerator();
      this.observeVisibility();
    },
    'localStyle.value': function setLocals() {
      this.setDisplay();
    },
    dropletObserver(id) {
      if (this.droplet._id === id) {
        this.observeVisibility();
        this.$store.dispatch('setDropletObserver', false);
      }
    },
  },
  mounted() {
    this.callCssGenerator();
    this.observeVisibility();
  },
  methods: {
    callCssGenerator() {
      return this.generateCSS({
        drp: this.droplet,
      });
    },
    observeVisibility() {
      const display = this.displayStatus;
      if (display[this.viewport] === 'none' || (display[this.viewport] === 'default' && display.desktop === 'none')) {
        this.hidden = true;
      } else {
        this.hidden = false;
      }
    },
    truncate(text) {
      if (!this.droplet.truncate) {
        return text || '';
      }
      const preview = text || '';
      const limit = parseInt(this.droplet.truncateLimit || 2, 10);
      if (preview.length < limit) {
        return preview;
      }
      return `${preview.substring(0, limit)}...`;
    },
    setDisplay() {
      if (this.activeDroplet._id === this.droplet._id) {
        const styles = this.localStyle;
        if (styles.properties.length > 0) {
          _.forEach(styles.properties, (property) => {
            this.$set(this.display, property, styles.value);
          });
        } else {
          _.forOwn(this.display, (value, key) => {
            this.display[key] = null;
          });
        }
      }
    },
    toggleBubble() {
      if (!this.roamingTemplate) {
        this.showEditBubble = !this.showEditBubble;
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/droplets/text-blob/index.js